import { byte } from '../types';

/**
 * Printer UI. The "paper" is bound to the element selected by the input.
 *
 * Every line that is output to the printer is added as a <div> to the paper.
 * The high bit of all characters is stripped and only visible characters are
 * added to the output. The following characters receive special treatment:
 *
 * *   `EOT` (ASCII 4): deletes last character
 * *   `HT` (ASCII 9): replaced with 8 spaces
 * *   `LF` (ASCII 10): silently removed
 * *   `CR` (ASCII 13): a newline and carriage return
 *
 * ---------------------------------------------------------------------------
 * SOUND
 *
 * A dot matrix head is nine pins hitting a ribbon, so a character is a burst
 * rather than a click, and a carriage return is the head being thrown back
 * across the platen followed by the paper advancing. Both are generated here
 * rather than sampled: the noise is shaped filtered white noise, which is
 * cheap, has no licensing attached to it, and lets every strike differ
 * slightly from the last so a page does not sound like a loop.
 *
 * WHY THE CLICKS HAVE THEIR OWN CLOCK
 *
 * A real MX-80 manages eighty characters a second. The emulated machine hands
 * over a whole line faster than that in an instant, so playing a burst as each
 * character arrives produces a buzz, not a printer.
 *
 * So characters are scheduled rather than played: each one is booked onto a
 * timeline at a fixed interval from the one before, and the sound runs on
 * after the text has finished appearing -- which is exactly what a printer
 * with a buffer does, and is what makes it recognisable.
 *
 * The timeline is capped. A program that dumps ten thousand characters would
 * otherwise queue two minutes of clattering that nobody can stop, so once the
 * schedule runs far enough ahead the extra strikes are dropped. The page is
 * still correct; only the noise is abridged.
 * ---------------------------------------------------------------------------
 */

/* ---------------------------------------------------------------------------
 * TUNING
 *
 * All of it, in one place, because getting a printer to sound like a printer
 * is done by ear and not by arithmetic. Change a number, print a letter,
 * change it again. Nothing below depends on anything else being right.
 * ------------------------------------------------------------------------- */

/** Characters per second. An ImageWriter II in draft manages about this. */
const CPS = 120;

/**
 * How long a single strike rings, in seconds.
 *
 * The important number. Shorter than the character interval and the page
 * ticks; longer and the strikes overlap into the rasp a real head makes. At
 * 120 cps the interval is 8ms, so 35ms means three strikes sounding at once.
 */
const STRIKE_RING = 0.07;

/** The pins themselves: a hard, bright transient. */
const PIN_HZ = 6000;
const PIN_SPREAD = 900;        // randomised either side, per strike
const PIN_GAIN = 0.5;

/**
 * The case and platen ringing after the pins hit.
 *
 * This is what stops it sounding like a click track. Low, resonant, and
 * quieter than the pins -- it should be felt rather than heard.
 */
const BODY_HZ = 400;
const BODY_Q = 7;
const BODY_GAIN = 0.03;

/** The carriage motor, running whenever the head is moving. */
const MOTOR_HZ = 3000;
const MOTOR_Q = 4;
const MOTOR_GAIN = 0.012;
const MOTOR_FADE = 0.09;       // in and out, seconds
const MOTOR_LINGER = 0.25;     // keeps running this long after the last strike

/** How far ahead the schedule may run, in seconds. */
const MAX_LOOKAHEAD = 2;

/**
 * Loudness by character, roughly.
 *
 * A W fires most of the nine pins; a full stop fires two. Not measured, just
 * sorted into three buckets by eye, which is enough for the ear to notice the
 * page has texture.
 */
const HEAVY = 'MWQ@#%&8BNHKRXZ0';
const LIGHT = " .,'`:;-_|!i";

function weight(c: string): number {
    if (HEAVY.includes(c.toUpperCase())) return 1.0;
    if (LIGHT.includes(c)) return 0.45;
    return 0.72;
}

class PrinterAudio {
    private ctx: AudioContext | null = null;
    private noise: AudioBuffer | null = null;
    private nextAt = 0;

    /* The motor is one long-running source, faded rather than started and
     * stopped -- a source that starts and stops per line clicks at the edges,
     * which is the very thing it is there to hide. */
    private motor: AudioBufferSourceNode | null = null;
    private motorGain: GainNode | null = null;
    private motorUntil = 0;

    public enabled = true;

    constructor() {
        try {
            this.enabled = window.localStorage.getItem('printerSound') !== 'off';
        } catch (e) {
            // Private browsing, or no storage. Sound stays on.
        }
    }

    setEnabled(on: boolean) {
        this.enabled = on;
        try {
            window.localStorage.setItem('printerSound', on ? 'on' : 'off');
        } catch (e) {
            // Nothing to be done, and nothing that matters.
        }
        if (!on) {
            this.nextAt = 0;
            this.stopMotor(true);
        }
    }

    private context(): AudioContext | null {
        if (this.ctx) {
            if (this.ctx.state === 'suspended') void this.ctx.resume();
            return this.ctx;
        }
        try {
            const AC = window.AudioContext ||
                (window as unknown as { webkitAudioContext: typeof AudioContext })
                    .webkitAudioContext;
            if (!AC) return null;
            this.ctx = new AC();
        } catch (e) {
            return null;
        }
        return this.ctx;
    }

    private noiseBuffer(ctx: AudioContext): AudioBuffer {
        if (this.noise) return this.noise;
        const len = ctx.sampleRate;
        const buf = ctx.createBuffer(1, len, ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
        this.noise = buf;
        return buf;
    }

    private slot(ctx: AudioContext, seconds: number): number | null {
        const now = ctx.currentTime;
        if (this.nextAt < now) this.nextAt = now;
        if (this.nextAt - now > MAX_LOOKAHEAD) return null;
        const at = this.nextAt;
        this.nextAt += seconds;
        return at;
    }

    /**
     * Keep the carriage motor running until at least `until`.
     *
     * Called by every strike, so a line of text holds it on and it fades a
     * quarter of a second after the last character -- which is roughly how
     * long a real head takes to come to rest.
     */
    private motorUntilAt(ctx: AudioContext, until: number) {
        this.motorUntil = Math.max(this.motorUntil, until);

        if (!this.motor) {
            const src = ctx.createBufferSource();
            src.buffer = this.noiseBuffer(ctx);
            src.loop = true;

            const band = ctx.createBiquadFilter();
            band.type = 'bandpass';
            band.frequency.value = MOTOR_HZ;
            band.Q.value = MOTOR_Q;

            const gain = ctx.createGain();
            gain.gain.setValueAtTime(0, ctx.currentTime);

            src.connect(band).connect(gain).connect(ctx.destination);
            src.start();

            this.motor = src;
            this.motorGain = gain;
        }

        const g = this.motorGain!;
        const now = ctx.currentTime;
        g.gain.cancelScheduledValues(now);
        g.gain.setValueAtTime(g.gain.value, now);
        g.gain.linearRampToValueAtTime(MOTOR_GAIN, now + MOTOR_FADE);
        g.gain.setValueAtTime(MOTOR_GAIN, this.motorUntil);
        g.gain.linearRampToValueAtTime(0, this.motorUntil + MOTOR_FADE);
    }

    private stopMotor(immediate = false) {
        if (!this.motor || !this.ctx) return;
        const now = this.ctx.currentTime;
        const g = this.motorGain!;
        g.gain.cancelScheduledValues(now);
        g.gain.setValueAtTime(g.gain.value, now);
        g.gain.linearRampToValueAtTime(0, now + (immediate ? 0.02 : MOTOR_FADE));
        const src = this.motor;
        this.motor = null;
        this.motorGain = null;
        try {
            src.stop(now + (immediate ? 0.04 : MOTOR_FADE + 0.02));
        } catch (e) {
            // Already stopped. Nothing to do.
        }
    }

    /**
     * One character.
     *
     * Two voices from the same noise: the pins, bright and short, and the
     * case ringing under them for three times as long. They are scheduled
     * together and decay together, and successive characters overlap.
     */
    strike(ch: string) {
        if (!this.enabled) return;
        const ctx = this.context();
        if (!ctx) return;

        const at = this.slot(ctx, 1 / CPS);
        if (at === null) return;

        const w = weight(ch);
        this.motorUntilAt(ctx, at + MOTOR_LINGER);

        // The pins.
        const pin = ctx.createBufferSource();
        pin.buffer = this.noiseBuffer(ctx);
        pin.loop = true;

        const pinBand = ctx.createBiquadFilter();
        pinBand.type = 'bandpass';
        pinBand.frequency.value = PIN_HZ + (Math.random() - 0.5) * PIN_SPREAD;
        pinBand.Q.value = 1.4;

        const pinGain = ctx.createGain();
        pinGain.gain.setValueAtTime(0, at);
        pinGain.gain.linearRampToValueAtTime(PIN_GAIN * w, at + 0.0006);
        pinGain.gain.exponentialRampToValueAtTime(0.0001, at + 0.010);

        pin.connect(pinBand).connect(pinGain).connect(ctx.destination);
        pin.start(at, Math.random() * 0.9);
        pin.stop(at + 0.015);

        // The case, ringing after them.
        const body = ctx.createBufferSource();
        body.buffer = this.noiseBuffer(ctx);
        body.loop = true;

        const bodyBand = ctx.createBiquadFilter();
        bodyBand.type = 'bandpass';
        bodyBand.frequency.value = BODY_HZ * (0.92 + Math.random() * 0.16);
        bodyBand.Q.value = BODY_Q;

        const bodyGain = ctx.createGain();
        bodyGain.gain.setValueAtTime(0, at);
        bodyGain.gain.linearRampToValueAtTime(BODY_GAIN * w, at + 0.002);
        bodyGain.gain.exponentialRampToValueAtTime(0.0001, at + STRIKE_RING);

        body.connect(bodyBand).connect(bodyGain).connect(ctx.destination);
        body.start(at, Math.random() * 0.9);
        body.stop(at + STRIKE_RING + 0.01);
    }

    /**
     * A carriage return: the head thrown back, then the paper pulled up.
     */
    carriage(columns: number) {
        if (!this.enabled) return;
        const ctx = this.context();
        if (!ctx) return;

        const travel = 0.05 + Math.min(columns, 80) / 80 * 0.10;

        const at = this.slot(ctx, travel + 0.03);
        if (at === null) return;

        this.motorUntilAt(ctx, at + travel + MOTOR_LINGER);

        const src = ctx.createBufferSource();
        src.buffer = this.noiseBuffer(ctx);
        src.loop = true;

        const band = ctx.createBiquadFilter();
        band.type = 'bandpass';
        band.Q.value = 2.5;
        band.frequency.setValueAtTime(700, at);
        band.frequency.linearRampToValueAtTime(2400, at + travel * 0.5);
        band.frequency.linearRampToValueAtTime(600, at + travel);

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, at);
        gain.gain.linearRampToValueAtTime(0.05, at + 0.010);
        gain.gain.setValueAtTime(0.05, at + travel * 0.75);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + travel);

        src.connect(band).connect(gain).connect(ctx.destination);
        src.start(at, Math.random() * 0.9);
        src.stop(at + travel + 0.01);

        // The head arriving at the margin, and the platen turning.
        const feedAt = at + travel;
        const feed = ctx.createBufferSource();
        feed.buffer = this.noiseBuffer(ctx);
        feed.loop = true;

        const low = ctx.createBiquadFilter();
        low.type = 'lowpass';
        low.frequency.value = 600;

        const fgain = ctx.createGain();
        fgain.gain.setValueAtTime(0, feedAt);
        fgain.gain.linearRampToValueAtTime(0.06, feedAt + 0.004);
        fgain.gain.exponentialRampToValueAtTime(0.0001, feedAt + 0.07);

        feed.connect(low).connect(fgain).connect(ctx.destination);
        feed.start(feedAt, Math.random() * 0.9);
        feed.stop(feedAt + 0.08);
    }
}

/**
 * The paper.
 *
 * Continuous feed: a sprocket strip down each edge with the holes punched
 * through it, and a line of perforations separating the strip from the part
 * that gets printed on. Drawn with gradients rather than an image so it scales
 * with the element and costs nothing to ship.
 *
 * Injected once, from here, so the whole printer remains one file.
 */
const PAPER_CSS = `
.paper {
  --strip: 26px;
  --pitch: 24px;
  --hole: 9px;
  --stock: #f6f3ea;
  --punch: #cfcabb;

  position: relative;
  background-color: var(--stock);
  padding-left: calc(var(--strip) + 14px);
  padding-right: calc(var(--strip) + 14px);

  /*
   * The sheet is as wide as the window, and no wider.
   *
   * Without this the element sizes to its widest line -- the lines are
   * white-space: pre and do not wrap -- so an eighty column printout on a
   * narrow window pushed the right hand edge off screen, taking the sprocket
   * strip with it. The paper appeared to have one side.
   *
   * border-box so the padding that makes room for the strips is counted
   * inside the width rather than added to it, and overflow hidden so a line
   * too long for the window runs under the right hand strip and stops at the
   * edge of the paper instead of spilling onto the desk.
   */
  box-sizing: border-box;
  width: calc(100% - var(--sbw, 0px));
  max-width: 100%;
  overflow: hidden;

  background-image:
    /* sprocket holes, left and right */
    radial-gradient(circle at center,
      var(--punch) 0 calc(var(--hole) / 2),
      transparent calc(var(--hole) / 2 + 0.5px)),
    radial-gradient(circle at center,
      var(--punch) 0 calc(var(--hole) / 2),
      transparent calc(var(--hole) / 2 + 0.5px)),
    /* the perforation each strip tears off along */
    repeating-linear-gradient(to bottom,
      var(--punch) 0 3px, transparent 3px 7px),
    repeating-linear-gradient(to bottom,
      var(--punch) 0 3px, transparent 3px 7px);

  background-size:
    var(--strip) var(--pitch),
    var(--strip) var(--pitch),
    1px 7px,
    1px 7px;

  background-position:
    0 calc(var(--pitch) / 2),
    100% calc(var(--pitch) / 2),
    var(--strip) 0,
    calc(100% - var(--strip)) 0;

  background-repeat: repeat-y, repeat-y, repeat-y, repeat-y;
}

/*
 * The strips sit a shade darker than the sheet. Real fanfold is one piece of
 * paper, but the edges pick up shadow from the tractor feed and it reads as
 * wrong without something there.
 */
.paper::before,
.paper::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: var(--strip);
  background: rgba(0, 0, 0, 0.035);
  pointer-events: none;
}
.paper::before { left: 0; }
.paper::after { right: 0; }

.paper .line {
  position: relative;
  z-index: 1;
  min-height: 1em;
  white-space: pre;
}
`;

let cssInjected = false;

function injectPaperCSS() {
    if (cssInjected) return;
    cssInjected = true;
    const style = document.createElement('style');
    style.textContent = PAPER_CSS;
    document.head.appendChild(style);
}

export default class Printer {
    private paper: HTMLElement;
    private _lineBuffer = '';
    private _line: HTMLElement;
    private _rawLen = 0;
    private _raw = new Uint8Array(1024);
    private _audio = new PrinterAudio();
    private _lineCount = 0;

    /**
     * Creates a new printer bound to the given element.
     * @param {string} el The selector of the element on which to bind the "paper".
     */
    constructor(el: string) {
        this.paper = document.querySelector(el)!;
        this.paper.classList.add('paper');
        injectPaperCSS();
        this.fitScrollbar();
        window.addEventListener('resize', () => this.fitScrollbar());
        this.newLine();
    }

    /**
     * Keep the sprocket strip out from under the scrollbar.
     *
     * With classic scrollbars there is nothing to do: the bar takes its
     * width out of the container, clientWidth already excludes it, and a
     * sheet at 100% stops short of it on its own.
     *
     * Overlay scrollbars -- macOS, and increasingly elsewhere -- take no
     * width at all and are drawn on top of the content. The sheet
     * therefore runs the full width and the bar sits squarely on the
     * right hand row of sprocket holes, which is the one part of the
     * page that is meant to look like paper.
     *
     * So: measure. If the container reserves nothing and the content
     * overflows, the bar is an overlay and the sheet gives up its own
     * width instead.
     */
    private fitScrollbar() {
        let el: HTMLElement | null = this.paper.parentElement;
        while (el && el !== document.body) {
            const oy = getComputedStyle(el).overflowY;
            if (oy === 'auto' || oy === 'scroll') break;
            el = el.parentElement;
        }
        const scroller = el || (document.scrollingElement as HTMLElement | null);
        if (!scroller) return;

        const reserved = scroller.offsetWidth - scroller.clientWidth;
        const overflowing = scroller.scrollHeight > scroller.clientHeight + 1;
        const overlay = reserved === 0 && overflowing;

        this.paper.style.setProperty('--sbw', overlay ? '14px' : '0px');
    }

    /** For a menu item. Returns what it was set to, for the caller's checkbox. */
    setSound(on: boolean) {
        this._audio.setEnabled(on);
        return on;
    }

    soundEnabled() {
        return this._audio.enabled;
    }

    private newLine() {
        // A page that did not overflow a moment ago may do so now, and
        // the scrollbar appears the instant it does.
        if ((this._lineCount++ & 7) === 0) this.fitScrollbar();

        this._line = document.createElement('div');
        this._line.classList.add('line');
        this._line.innerText = this._lineBuffer;
        this.paper.append(this._line);
        this._lineBuffer = '';
    }

    putChar(val: byte) {
        const ascii = val & 0x7f;
        const visible = val >= 0x20;
        const c = String.fromCharCode(ascii);

        if (c === '\r') {
            // Sounded before the buffer is cleared: how far the head has to
            // come back depends on how much was printed.
            this._audio.carriage(this._lineBuffer.length);
            this.newLine();
        } else if (c === '\n') {
            // eat for now
        } else if (c === '\t') {
            // possibly not right due to tab stops
            this._lineBuffer += '        ';
            this._audio.strike(' ');
        } else if (ascii === 0x04) {
            this._lineBuffer = this._lineBuffer.slice(0, -1);
        } else if (visible) {
            this._lineBuffer += c;
            // A space is the head stepping without firing a pin, so it is
            // quieter -- but not silent, because the carriage still moves and
            // the motor is what carries the sound between characters.
            this._audio.strike(c);
        }
        this._line.innerText = this._lineBuffer;
        this._raw[this._rawLen] = val;
        this._rawLen++;
        if (this._rawLen > this._raw.length) {
            const newRaw = new Uint8Array(this._raw.length * 2);
            newRaw.set(this._raw);
            this._raw = newRaw;
        }
    }

    clear() {
        this._lineBuffer = '';
        this.paper.innerHTML = '';
        this.newLine();
        this._raw = new Uint8Array(1024);
        this._rawLen = 0;
    }

    hasPrintout() {
        return this.paper.innerText.length > 0;
    }

    getRawOutput() {
        return this._raw.slice(0, this._rawLen);
    }
}
